import { useState } from "react";
import type { Exemplar, ChecklistItem } from "../../api/content";
import type { PackageView } from "../../api/package";
import type { CheckItem, RatePackage } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { inputShell } from "../../components/Field";
import { FileButton } from "../../components/FileButton";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { StepChecklist } from "../../components/StepChecklist";
import { OptionTile } from "../../components/OptionTile";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { money, numberOrNull } from "../../lib/format";

export const SELF_MIN = 7;
const CV_MAX_BYTES = 8 * 1024 * 1024;

interface PackageScreenProps {
  view: PackageView;
  track: { label: string; host: string; tip: string };
  exemplar: Exemplar;
  checklist: ChecklistItem[];
  cvBuilders: { name: string; url: string }[];
  verifyResult: { checks: CheckItem[]; keptPrevious: boolean } | null;
  uploading: boolean;
  uploadError: string | null;
  busy: boolean;
  onVerifyPortfolio: (url: string) => void;
  onToggleSelf: (keys: string[]) => void;
  onRateChange: (target: number | null, hours: number | null, packages: RatePackage[]) => void;
  onUploadCv: (file: File) => void;
  onComplete: () => void;
}

/** A fictional model portfolio with numbered pins that map to the 10-point checklist. */
function ExemplarMock({ exemplar, checklist }: { exemplar: Exemplar; checklist: ChecklistItem[] }) {
  const pin = (key: string) => (
    <span className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-kicker text-[10px] font-bold text-white">
      {checklist.findIndex((c) => c.key === key) + 1}
    </span>
  );
  return (
    <div className="overflow-hidden rounded-[var(--radius-control)] border border-border">
      <div className="bg-surface-high px-3 py-1.5 text-[12px] text-text-muted">
        {pin("open")}
        {exemplar.home}
      </div>
      <div className="flex flex-col gap-3 p-4 text-[13px]">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-accent font-bold text-on-accent">
            {exemplar.init}
          </span>
          <div>
            <div className="font-bold">
              {pin("photo")}
              {exemplar.name}
            </div>
            <div className="text-text-muted">
              {pin("position")}
              {exemplar.line}
            </div>
          </div>
        </div>
        <div className="rounded-[9px] bg-accent-tint p-3">
          <div className="font-bold">
            {pin("best")}Featured: {exemplar.feature.title}
          </div>
          <div>
            {pin("casestudy")}
            <b>Problem:</b> {exemplar.feature.problem}
          </div>
          <div>
            <b>What I did:</b> {exemplar.feature.process}
          </div>
          <div>
            {pin("result")}
            <b>Result:</b> {exemplar.feature.result}
          </div>
        </div>
        <div>
          {pin("pieces")}
          {pin("context")}
          <span className="text-text-muted">More work, each shown in context</span>
          <div className="mt-1.5 grid grid-cols-3 gap-2">
            {exemplar.pieces.map((piece) => (
              <div key={piece} className="rounded-[7px] bg-surface-high p-2 text-[12px]">
                {piece}
              </div>
            ))}
          </div>
        </div>
        <div className="text-text-muted">
          {pin("contact")}
          {/* The mock's contact line uses &middot; entities; render it as plain text. */}
          {exemplar.contact.replace(/&middot;/g, "·")} {pin("recent")}Updated this month
        </div>
      </div>
    </div>
  );
}

function RateCard({
  view,
  onChange,
}: {
  view: PackageView;
  onChange: PackageScreenProps["onRateChange"];
}) {
  const step = view.step;
  const [target, setTarget] = useState(step.rateTarget?.toString() ?? "");
  const [hours, setHours] = useState(step.rateHours?.toString() ?? "");
  const [packages, setPackages] = useState<RatePackage[]>(
    step.packages.length
      ? step.packages
      : [{ name: "Starter" }, { name: "Standard" }, { name: "Premium" }],
  );

  const push = (nextTarget: string, nextHours: string, nextPackages: RatePackage[]) =>
    onChange(numberOrNull(nextTarget), numberOrNull(nextHours), nextPackages);

  const setPackage = (index: number, patch: Partial<RatePackage>) => {
    const next = packages.map((pkg, i) => (i === index ? { ...pkg, ...patch } : pkg));
    setPackages(next);
    push(target, hours, next);
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-[12.5px] font-bold">
          Monthly income goal from gigs (USD)
          <input
            type="number"
            min={0}
            className={`${inputShell} mt-1.5`}
            placeholder="e.g. 400"
            value={target}
            onChange={(event) => {
              setTarget(event.target.value);
              push(event.target.value, hours, packages);
            }}
          />
        </label>
        <label className="text-[12.5px] font-bold">
          Hours per week you can work on gigs
          <input
            type="number"
            min={0}
            className={`${inputShell} mt-1.5`}
            placeholder="e.g. 20"
            value={hours}
            onChange={(event) => {
              setHours(event.target.value);
              push(target, event.target.value, packages);
            }}
          />
        </label>
      </div>
      {view.rateSuggestion ? (
        <Banner tone="good">
          Aim for about <b>{money(view.rateSuggestion)} per hour</b>. That assumes only 60% of your
          gig time is paid work, the rest goes on finding clients and admin. A 10-hour project at
          this rate is about {money(view.rateSuggestion * 10)}.
        </Banner>
      ) : null}
      {view.rateWarnings.map((warning) => (
        <Banner key={warning} tone="warn">
          {warning}
        </Banner>
      ))}
      <p className="mt-3 text-[13px] text-text-muted">
        Typical project budgets on the practice job board: {money(view.budgetRange.p25)} to{" "}
        {money(view.budgetRange.p75)} (middle {money(view.budgetRange.p50)}).
      </p>
      {packages.map((pkg, index) => (
        <div key={index} className="mt-3">
          <div className="grid grid-cols-[1.1fr_.8fr_.8fr] gap-2">
            <input
              aria-label={`Package ${index + 1} name`}
              className={inputShell}
              value={pkg.name ?? ""}
              onChange={(event) => setPackage(index, { name: event.target.value })}
            />
            <input
              aria-label={`Package ${index + 1} price (USD)`}
              type="number"
              min={0}
              placeholder="Price"
              className={inputShell}
              value={pkg.price ?? ""}
              onChange={(event) => setPackage(index, { price: numberOrNull(event.target.value) })}
            />
            <input
              aria-label={`Package ${index + 1} delivery (days)`}
              type="number"
              min={0}
              placeholder="Days"
              className={inputShell}
              value={pkg.days ?? ""}
              onChange={(event) => setPackage(index, { days: numberOrNull(event.target.value) })}
            />
          </div>
          <input
            aria-label={`Package ${index + 1} what's included`}
            placeholder="What's included, e.g. 1 logo, 3 concepts, 2 revisions, source files"
            className={`${inputShell} mt-2`}
            value={pkg.incl ?? ""}
            onChange={(event) => setPackage(index, { incl: event.target.value })}
          />
        </div>
      ))}
      <Checks checks={view.rateChecks} />
    </>
  );
}

/** Step 2: Package. Portfolio, 10-point self-check, rate card and CV. */
export function PackageScreen(props: PackageScreenProps) {
  const { view } = props;
  const [url, setUrl] = useState(view.step.portfolioUrl ?? "");
  const [cvError, setCvError] = useState<string | null>(null);
  const selected = new Set(view.step.selfCheck);
  const rateDone = view.rateChecks.every((check) => check.pass);

  const pickCv = (file: File | undefined) => {
    setCvError(null);
    if (!file) return;
    if (!/\.(pdf|docx?)$/i.test(file.name))
      return setCvError("That needs to be a PDF or Word document.");
    if (file.size === 0) return setCvError("That file looks empty.");
    if (file.size > CV_MAX_BYTES) return setCvError("That file is too large, keep it under 8MB.");
    props.onUploadCv(file);
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 2 · Package"
        title="Show proof and name your price."
        sub="A strong portfolio, a clear rate card and a CV are what turn a profile into a hire. Start by seeing what good looks like."
      />
      <Guide guideKey="gig2" done={view.done} />
      <StepChecklist gate={view.gate} done={view.done} />

      <Section
        title={`1. What a hire-winning ${props.track.label} portfolio looks like`}
        hint="A fictional example. Each number marks one of the 10 things clients (and your peers in Step 4) look for."
      >
        <ExemplarMock exemplar={props.exemplar} checklist={props.checklist} />
        <ol className="mt-3 grid grid-cols-1 gap-1 pl-5 text-[13px] sm:grid-cols-2">
          {props.checklist.map((item) => (
            <li key={item.key}>
              <b>{item.t}.</b> {item.d}
            </li>
          ))}
        </ol>
        <Banner tone="note">
          <b>Where to put it:</b> {props.track.tip}
          <br />
          <span className="text-[13px] text-text-muted">Accepted homes: {props.track.host}.</span>
        </Banner>
        <Banner tone="note">
          <b>No paid clients yet?</b> Do a self-initiated project for a real local business and
          label it "Concept". Show your process and any honest result. Never invent clients or
          numbers, peers and clients can tell.
        </Banner>
      </Section>

      <Section
        title="2. Your portfolio link"
        hint="We check it opens, isn't raw file storage, and is the right kind of home for your skill."
        status={<Pill ok={view.step.portfolioOk} okText="Verified" />}
      >
        <div className="flex flex-wrap gap-2">
          <input
            type="url"
            aria-label="Your portfolio link"
            placeholder="https://..."
            className={`${inputShell} min-w-[220px] flex-1`}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <Button
            size="sm"
            disabled={!url.trim()}
            onClick={() => props.onVerifyPortfolio(url.trim())}
          >
            Verify
          </Button>
        </div>
        <Checks checks={props.verifyResult?.checks ?? view.portfolioChecks} />
        {props.verifyResult?.keptPrevious ? (
          <p className="mt-1.5 text-[12.5px] text-text-muted">
            Your previously verified link is kept: {view.step.portfolioUrl}
          </p>
        ) : null}
      </Section>

      <Section
        title="3. Check it against the 10 points"
        hint={`Open your portfolio and check only what's genuinely there. You need at least ${SELF_MIN}. Your peers check the same list in Step 4, so be honest.`}
        status={
          <Pill
            ok={view.selfCount >= SELF_MIN}
            okText={`${view.selfCount} of 10`}
            noText={`${view.selfCount} of 10`}
          />
        }
      >
        <div className="flex flex-col gap-2">
          {props.checklist.map((item) => (
            <OptionTile
              key={item.key}
              selected={selected.has(item.key)}
              title={item.t}
              detail={item.d}
              onToggle={() => {
                const next = new Set(selected);
                if (next.has(item.key)) next.delete(item.key);
                else next.add(item.key);
                props.onToggleSelf(Array.from(next));
              }}
            />
          ))}
        </div>
      </Section>

      <Section
        title="4. Your rate card"
        hint="First, what are you worth? Then turn it into packages a client can pick from."
        status={<Pill ok={rateDone} />}
      >
        <RateCard view={view} onChange={props.onRateChange} />
      </Section>

      <Section
        title="5. Your CV"
        hint="A one-page CV for platforms and clients that ask for one."
        status={<Pill ok={view.step.hasCv} okText="On file" />}
      >
        <FileButton
          label="Upload my CV"
          ariaLabel="Upload your CV"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          busy={props.uploading}
          hasFile={view.step.hasCv}
          onFile={pickCv}
        />
        <p className="mt-2 text-[13px] text-text-muted">
          {props.uploading
            ? "Uploading..."
            : view.step.hasCv
              ? `✓ ${view.step.cvName ?? "Your CV"} on file.`
              : "PDF or Word document, under 8MB."}
        </p>
        {cvError || props.uploadError ? (
          <p role="alert" className="text-[13px] text-negative">
            {cvError ?? props.uploadError}
          </p>
        ) : null}
        <p className="mt-2 text-[13px] text-text-muted">No CV yet? Build one free:</p>
        <div className="flex flex-wrap gap-2">
          {props.cvBuilders.map((builder) => (
            <a
              key={builder.name}
              href={builder.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-8 items-center rounded-[var(--radius-chip)] border border-border px-3 text-[12.5px] font-semibold text-accent"
            >
              {builder.name} ↗
            </a>
          ))}
        </div>
      </Section>

      {view.gate.pass && !view.done ? (
        <Banner tone="good">
          ✓ Portfolio verified, checklist done, rate card set and CV on file. You can complete Step
          2.
        </Banner>
      ) : null}
      <GateFooter
        gate={view.gate}
        done={view.done}
        label="Complete Step 2"
        next={{ to: "/gig/3", label: "Continue to Step 3" }}
        busy={props.busy}
        onComplete={props.onComplete}
      />
    </div>
  );
}
